'use client';

import { SyntheticEvent, useEffect, useRef } from 'react';

import { useToggle } from '@/hooks/use-toggle';
import { CodeTitle } from '@/types/general';

interface SelectProps<T extends CodeTitle> {
    options: T[];
    defaultValue: T;
    icon?: string;
    onClick: (option: T) => void;
    className?: { [key: string]: string };
    textClassName?: string;
    testId?: string;
}

export default function Select<T extends CodeTitle = CodeTitle>(props: SelectProps<T>) {
    const { toggleValue, handleToggle, setToggleValue } = useToggle();

    useEffect(() => {
        if (props.options && props.options.length === 0) {
            setToggleValue(false);
        }
    }, [props.options, setToggleValue]);

    const handleSelect = (e: SyntheticEvent, option: T) => {
        e.stopPropagation();
        props.onClick(option);
        setToggleValue(false);
    };

    const selectRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                setToggleValue(false);
            }
        };

        document.addEventListener('click', handleOutsideClick);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [setToggleValue]);

    return (
        <div
            ref={selectRef}
            className={`text-regular relative z-[1] flex min-w-[120px] ${props.className} ${props.textClassName ?? 'text-sm'}`}
        >
            <div
                data-testid={props.testId}
                onClick={handleToggle}
                className={`flex w-full min-w-[6rem] justify-between gap-x-2 md:px-2 ${
                    props.className && props.className.select
                }`}
            >
                {props.icon && (
                    <span
                        className="icon border-regular !min-h-6 !min-w-6 h-6 w-6 rounded-full bg-cover"
                        style={{ backgroundImage: `url(/assets/icons/${props.icon}-${props.defaultValue.code}.svg)` }}
                    />
                )}
                <button type="button" className={`mr-2 py-2 ${props.className} ${props.textClassName ?? 'text-sm'}`}>
                    {props.defaultValue.title}
                </button>
                <span className="icon w-3 cursor-pointer bg-extend" />
            </div>
            <ul
                className={`no-scrollbar absolute right-1/2 top-[125%] z-[1] flex max-h-[155px] w-full translate-x-1/2 flex-col items-start overflow-y-auto rounded-md border border-black border-opacity-40 bg-white py-4 ${
                    !toggleValue || props.options.length === 0 ? 'hidden' : ''
                }`}
            >
                {props.options.map((option: T) => (
                    <div
                        data-testid={`${props.testId}-${option.code}`}
                        key={option.code}
                        className="no-scrollbar flex w-full cursor-pointer justify-between gap-x-2 px-5 py-2 transition-colors duration-100 hover:bg-accent hover:bg-opacity-20"
                        onClick={(e) => handleSelect(e, option)}
                    >
                        {props.icon && (
                            <div className="flex items-center justify-start">
                                <span
                                    className="icon min-h-5 min-w-5 border-regular box-content h-5 w-5 rounded-full"
                                    style={{ backgroundImage: `url(/assets/icons/${props.icon}-${option.code}.svg)` }}
                                />
                            </div>
                        )}
                        <button className="w-full text-left">{option.title}</button>
                    </div>
                ))}
            </ul>
        </div>
    );
}
